import { useRouter } from "next/router";
import { CSSProperties, useEffect, useRef, useState } from "react";
import { composeClassNames } from "@faedev/editor-utils";
import Image from "next/image";
import { Alert, Col, Row } from "react-bootstrap";
import ReactMarkdown from "react-markdown";
import Heading from "src/components/Heading";
import Hr from "src/components/Hr";
import IntroductionActions from "src/components/IntroductionActions";
import IntroductionInfo from "src/components/IntroductionInfo";
import Preloader from "src/components/Preloader";
import Price from "src/components/Price";
import PriceVariants from "src/components/PriceVariants";
import PublishButtons from "src/components/PublishButtons";
import ReadMore from "src/components/ReadMore";
import RichText from "src/components/RichText";
import SellButtons from "src/components/SellButtons";
import Text from "src/components/Text";
import Translate from "src/components/Translate";
import constants from "src/constants";
import getLink from "src/functions/getLink";
import gutter from "src/functions/gutter";
import loadCustom from "src/functions/loadCustom";
import profileNameText from "src/functions/profileNameText";
import translate from "src/functions/translate";
import Check from "src/icons/Check";
import { sAlert } from "src/styles/sAlert.module.scss";
import { sClassIntroduction } from "src/styles/sClassIntroduction.module.scss";
import { sIntroduction } from "src/styles/sIntroduction.module.scss";
import { sMuxThumbnail } from "src/styles/sMuxThumbnail.module.scss";
import { ApiMerch } from "src/types/ApiMerch";

type Props = {
  style?: CSSProperties | null | undefined;
  className?: (string | string) | null | undefined;
  data: ApiMerch;
  freshDataLoaded: boolean;
};

function MerchIntroduction({ style, className, data, freshDataLoaded }: Props) {
  const { locale: lang } = useRouter();
  const [newPrice, setNewPrice] = useState(data?.price);
  const [priceVariants, setPriceVariants] = useState(null);
  const titleRef = useRef(null);

  useEffect(() => {
    const pv = data && loadCustom(data?.id, "class", "priceVariants");
    pv && setPriceVariants(pv);
  }, [data, data?.id]);

  var profileDefined =
    data.profile && Object.keys(data.profile).length > 0 ? true : false;

  return data ? (
    <Row
      style={style}
      className={composeClassNames([
        sIntroduction,
        sClassIntroduction,
        className,
        "class-introduction",
      ])}
      ref={titleRef}
    >
      <Col className={`class-preview`} lg={7}>
        <div
          className={composeClassNames([sMuxThumbnail, "mux-thumbnail-square"])}
        >
          <Image
            className="mux-thumbnail-image"
            src={data.thumbnail.url}
            alt={data.title}
            objectFit="contain"
            layout="fill"
          />
        </div>

        <PublishButtons className={`class-buttons`} />
      </Col>
      <Col className={"class-detail"} lg={5}>
        <Heading type={`h1`} title={data.title} />
        {profileDefined && (
          <IntroductionInfo
            type="class"
            data={[
              {
                item: profileNameText(
                  data.profile.client?.name,
                  data.profile.client?.surname,
                  data.profile.nickname,
                  data.profile.type
                ),
                url: getLink(data.profile.locale, `profile`, data.profile.slug),
              },
              { item: null },
              { item: String(Math.round(data.views / 100)) },
            ]}
          />
        )}
        <IntroductionActions
          data={{
            favorite: true,
            rating: typeof data.rating === "number" ? data.rating : 0,
            paid: data.paid,
            isPaid: data.isPaid,
            subscription: data?.subscription,
          }}
        />
        {freshDataLoaded ? (
          <>
            {data.isPaid && (
              <Alert
                variant="success"
                className={composeClassNames([sAlert])}
                style={gutter(`margin`, `top`, `lg`)}
              >
                <Check /> <Translate tKey={`You have purchased the class.`} />
              </Alert>
            )}
            {data.price &&
              (!priceVariants ? (
                <Price
                  currency={`CZK`}
                  price={data.price}
                  priceBasic={data.price_basic} // TODO: currency should be dynamic AND implement price basic
                  size="lg"
                />
              ) : (
                <PriceVariants
                  currency={
                    priceVariants?.currency ||
                    constants.Locales._default.currency
                  }
                  price={data.price}
                  priceAlternatives={priceVariants?.prices}
                  /* @ts-ignore */
                  setNewPrice={setNewPrice}
                  size="xm"
                  title={
                    priceVariants?.title &&
                    translate(priceVariants?.title, lang)
                  }
                />
              ))}
            {!data.isPaid && constants.Show.sale && data.price && (
              <>
                <SellButtons
                  entityId={data.id}
                  inCart={data.inCart}
                  id={1}
                  newPrice={newPrice ? newPrice : null}
                  orderId={""}
                  type={"merch"} // TODO: there is "lecture", but should be class instead
                  size="lg"
                  btnPrimary={
                    priceVariants?.btnPrimary || {
                      display: true,
                      variant: "primary",
                    }
                  }
                  btnSecondary={priceVariants?.btnSecondary || undefined}
                />
                {data.inCart && (
                  <Alert
                    variant={`info`}
                    className={composeClassNames([sAlert])}
                    style={gutter(`margin`, `top`, `md`)}
                  >
                    <Translate tKey={`Content is already in the cart.`} />
                  </Alert>
                )}
              </>
            )}
          </>
        ) : (
          <Text
            size={`md`}
            style={{ display: "flex", alignItems: "center", gap: "10px" }}
          >
            <Preloader />
            <Translate tKey={`Loading prices`} />
          </Text>
        )}
      </Col>
      {data.description.length > 0 ? (
        <Col className={`class-description`} lg={12}>
          <Hr variant={`light`} />
          <ReadMore
            rowsVisible={constants.Text.descriptionRowsShort}
            rowsCount={
              data.description.length / constants.Text.descriptionCharsPerRow
            }
          >
            <RichText size={`xn`}>
              <ReactMarkdown>{data.description}</ReactMarkdown>
            </RichText>
          </ReadMore>
        </Col>
      ) : null}
    </Row>
  ) : null;
}

export default MerchIntroduction;
